import React, { useState, useMemo } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { FEATURES, FeatureKey } from '../utils/features';
import { getCurrencyFromLocale, getFormattedPrices, getSubscribeButtonText } from '../utils/pricing';
import EmbeddedCheckoutModal from './EmbeddedCheckout';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerFeature?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, triggerFeature }) => {
  const { setDevMode } = useSubscription();
  const { user } = useAuth();
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly'); // Default to yearly (better value)
  
  // Detect currency based on user's locale
  const currency = useMemo(() => getCurrencyFromLocale(), []);
  const prices = useMemo(() => getFormattedPrices(currency), [currency]);

  if (!isOpen) return null;

  // If checkout is shown, render the embedded checkout modal
  if (showCheckout) {
    return (
      <EmbeddedCheckoutModal
        isOpen={true}
        onClose={() => setShowCheckout(false)}
        billingPeriod={selectedPlan}
        currency={currency}
        onSuccess={() => {
          // Premium is already set in localStorage by EmbeddedCheckout
          // Stripe will redirect to return_url automatically
          // No need for alert - the return page will show success message
          setDevMode(true);
        }}
      />
    );
  }

  // Organize features for comparison table
  const allFeatures: FeatureKey[] = [
    'basicChartEditor',
    'annotationsVisibility', 
    'annotationsEditing',
    'localSave',
    'cloudSync',
    'gigMode',
    'setlistManager',
    'unlimitedSetlists',
    'jamSession',
    'pdfExport',
    'bandWorkspace',
    'chartTemplates',
    'prioritySupport'
  ];

  const getFeatureDisplay = (featureKey: FeatureKey, plan: 'free' | 'premium') => {
    const feature = FEATURES[featureKey];
    const isAvailable = feature.plans.includes(plan);
    
    if (featureKey === 'basicChartEditor') {
      if (plan === 'free') return { available: true, text: '15 charts max', icon: '✓' };
      if (plan === 'premium') return { available: true, text: 'Unlimited', icon: '✓' };
    }
    
    if (featureKey === 'setlistManager') {
      if (plan === 'free') return { available: true, text: '1 setlist, 5 songs max', icon: '✓' };
      if (plan === 'premium') return { available: true, text: 'Unlimited', icon: '✓' };
    }
    
    if (featureKey === 'annotationsVisibility') {
      if (plan === 'free') return { available: true, text: 'View only', icon: '✓' };
      if (plan === 'premium') return { available: true, text: 'Full access', icon: '✓' };
    }
    
    if (featureKey === 'jamSession') {
      if (plan === 'free') return { available: true, text: '2 persons max', icon: '✓' };
      if (plan === 'premium') return { available: true, text: 'Unlimited', icon: '✓' };
    }

    return {
      available: isAvailable,
      text: '',
      icon: isAvailable ? '✓' : '❌'
    };
  };

  const handleUpgrade = () => {
    if (!user || !user.email) {
      alert('Please sign in first to upgrade to Premium!');
      return;
    }
    // Show embedded checkout instead of redirecting
    setShowCheckout(true);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[95vh] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl p-4 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold mb-1">🚀 Choose Your Plan</h2>
              <p className="text-blue-100 text-sm">
                Compare features and unlock professional music tools
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 text-xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[80vh]">
          {/* Trigger feature highlight */}
          {triggerFeature && FEATURES[triggerFeature as keyof typeof FEATURES] && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-1 text-sm">
                🎯 You tried to use: {FEATURES[triggerFeature as keyof typeof FEATURES].name}
              </h3>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {FEATURES[triggerFeature as keyof typeof FEATURES].description}
              </p>
            </div>
          )}

          {/* Plan Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              {/* Header */}
              <thead>
                <tr>
                  <th className="text-left p-2 font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 w-1/2">
                    Features
                  </th>
                  <th className="text-center p-2 border-b border-gray-200 dark:border-gray-600 w-1/4">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">Free</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">{prices.symbol}0/month</div>
                    </div>
                  </th>
                  <th className="text-center p-2 border-b border-gray-200 dark:border-gray-600 w-1/4">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-2 text-white">
                      <div className="text-sm font-bold">Premium</div>
                      <div className="text-xs">{prices.monthly}/month</div>
                    </div>
                  </th>
                </tr>
              </thead>

              {/* Feature rows */}
              <tbody>
                {allFeatures.map((featureKey) => {
                  const feature = FEATURES[featureKey];
                  const freeDisplay = getFeatureDisplay(featureKey, 'free');
                  const premiumDisplay = getFeatureDisplay(featureKey, 'premium');

                  return (
                    <tr key={featureKey} className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 ${triggerFeature === featureKey ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                      <td className="p-2">
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {feature.name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          {feature.description}
                        </div>
                      </td>
                      
                      {/* Free column */}
                      <td className="p-2 text-center">
                        <div className="flex flex-col items-center justify-center space-y-0.5">
                          <span className="text-lg font-bold text-green-600 dark:text-green-400">{freeDisplay.icon}</span>
                          {freeDisplay.text && (
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {freeDisplay.text}
                            </span>
                          )}
                        </div>
                      </td>
                      
                      {/* Premium column */}
                      <td className="p-2 text-center">
                        <div className="flex flex-col items-center justify-center space-y-0.5">
                          <span className="text-lg font-bold text-green-600 dark:text-green-400">{premiumDisplay.icon}</span>
                          {premiumDisplay.text && (
                            <span className="text-xs text-green-600 dark:text-green-400">
                              {premiumDisplay.text}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Billing Period Selection */}
          <div className="mt-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Choose Your Plan</h3>
            <div className="space-y-2">
              {/* Monthly Option */}
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`w-full p-3 rounded-lg border-2 transition-all flex justify-between items-center ${
                  selectedPlan === 'monthly'
                    ? 'border-blue-500 bg-blue-500/10 dark:bg-blue-500/20'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400'
                }`}
              >
                <div className="text-left">
                  <p className="text-gray-900 dark:text-white font-medium text-sm">Monthly</p>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">Billed monthly</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-900 dark:text-white font-bold text-sm">{prices.monthly}</p>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">/month</p>
                </div>
              </button>
              
              {/* Yearly Option */}
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`w-full p-3 rounded-lg border-2 transition-all flex justify-between items-center relative ${
                  selectedPlan === 'yearly'
                    ? 'border-green-500 bg-green-500/10 dark:bg-green-500/20'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400'
                }`}
              >
                <div className="absolute -top-2 left-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                  2 MONTHS FREE
                </div>
                <div className="text-left">
                  <p className="text-gray-900 dark:text-white font-medium text-sm">Yearly</p>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">Billed annually</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-900 dark:text-white font-bold text-sm">{prices.yearly}</p>
                  <p className="text-gray-600 dark:text-gray-400 text-xs">/year <span className="line-through text-gray-500">{prices.yearlyStrikethrough}</span></p>
                </div>
              </button>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-4 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 text-gray-600 dark:text-gray-300 py-2 px-4 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              Continue with Free
            </button>
            
            <button
              onClick={handleUpgrade}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-sm"
            >
              🚀 Subscribe - {getSubscribeButtonText(selectedPlan, currency)}
            </button>
          </div>

          {/* Development helper */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-3 p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg border border-yellow-300 dark:border-yellow-700">
              <div className="text-xs text-yellow-800 dark:text-yellow-300 font-medium mb-1">
                🛠️ Development Mode
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => { setDevMode(true); onClose(); }}
                  className="text-xs px-2 py-1 bg-green-500 text-white rounded"
                >
                  Simulate Premium
                </button>
                <button
                  onClick={() => { setDevMode(false); onClose(); }}
                  className="text-xs px-2 py-1 bg-gray-500 text-white rounded"
                >
                  Reset to Free
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;